import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import {
  MessageCircle,
  Brain,
  TrendingUp,
  Users,
  ArrowRight,
  Plus,
  Minus,
} from "lucide-react";
import "./AskTeamGrid.css";
import PublicHeader from "../../components/PublicHeader/PublicHeader";
import Testimonials from "../../components/Testimonials/Testimonials";
import ReadyToTransform from "../../components/ReadyToTransform/ReadyToTransform";
import Footer from "../../components/Footer/Footer";

const TITLE = "Ask TeamGrid | AI Powered Workforce Intelligence Platform";
const DESCRIPTION =
  "Turn work data into actionable intelligence. Ask questions in plain English and get real time AI insights on team productivity and performance.";
const CANONICAL = "https://teamgrid.ai/features/ask-teamgrid";
const OG_IMAGE = "https://teamgrid.ai/og/og-home.webp";

const AskTeamGrid = () => {
  const navigate = useNavigate();
  const [faqOpen, setFaqOpen] = useState({ 0: true });
  const toggleFaq = (i) => setFaqOpen((prev) => ({ ...prev, [i]: !prev[i] }));

  // FAQ data specific to Ask TeamGrid
  const askTeamGridFAQs = [
    {
      question: "How does Ask TeamGrid AI work?",
      answer:
        "Ask TeamGrid AI uses natural language processing to analyze your team's productivity data and provide instant insights. Simply ask questions in plain English and get actionable answers.",
    },
    {
      question: "What kind of questions can I ask?",
      answer:
        "You can ask about team productivity, project progress, individual performance, time allocation, bottlenecks, and recommendations for improvement. The AI understands natural language queries.",
    },
    {
      question: "Is the data secure and private?",
      answer:
        "Yes, all data is encrypted and only accessible to authorized team members. The AI operates within your secure environment and never shares data externally.",
    },
  ];

  const sampleQuestions = [
    {
      icon: Brain,
      text: "Who's stuck on their current tasks?",
      color: "text-purple-600",
      bgColor: "bg-purple-100",
    },
    {
      icon: TrendingUp,
      text: "Where are we losing time to manual work?",
      color: "text-blue-600",
      bgColor: "bg-blue-100",
    },
    {
      icon: Users,
      text: "Which projects have bottlenecks?",
      color: "text-green-600",
      bgColor: "bg-green-100",
    },
    {
      icon: MessageCircle,
      text: "Show me team productivity trends",
      color: "text-orange-600",
      bgColor: "bg-orange-100",
    },
  ];

  return (
    <div className="ask-teamgrid-container">
      <Helmet>
        <title>{TITLE}</title>
        <meta name="description" content={DESCRIPTION} />
        <link rel="canonical" href={CANONICAL} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={CANONICAL} />
        <meta property="og:title" content={TITLE} />
        <meta property="og:description" content={DESCRIPTION} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="og:site_name" content="TeamGrid" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={CANONICAL} />
        <meta name="twitter:title" content={TITLE} />
        <meta name="twitter:description" content={DESCRIPTION} />
        <meta name="twitter:image" content={OG_IMAGE} />
      </Helmet>
      <div className="relative min-h-screen overflow-hidden bg-white">
        <PublicHeader />
        <div className="pt-16 sm:pt-18 md:pt-20">
          {/* Background Effects */}
          <div
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(ellipse at top, rgba(0,209,193,0.05), #FFFFFF, #FFFFFF)",
            }}
          ></div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            {/* Hero: full-bleed gradient; content aligned with header */}
            <div
              className="relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] w-screen pt-16 sm:pt-18 md:pt-20 pb-10 sm:pb-12 md:pb-14 px-4 sm:px-6 md:px-8"
              style={{
                background:
                  "radial-gradient(141.42% 141.42% at 0% 0%, rgba(230, 242, 255, 0.5) 0%, rgba(233, 252, 250, 0) 50%), radial-gradient(141.42% 141.42% at 100% 100%, rgba(240, 247, 255, 0.5) 0%, rgba(240, 247, 255, 0) 50%), #FFFFFF",
              }}
            >
              <div className="max-w-7xl mx-auto">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  className="mb-0 text-center"
                >
                  <div className="flex items-center justify-center gap-3 mb-6">
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-purple-100 border border-purple-200">
                      <MessageCircle className="w-4 h-4 text-purple-600" />
                      <span className="text-sm font-medium text-gray-800">
                        AI ASSISTANT
                      </span>
                    </div>
                  </div>
                  <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-4">
                    Ask{" "}
                    <span className="text-[#00D1C1] font-semibold">
                      TeamGrid
                    </span>
                  </h1>
                  <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                    Get instant answers about your team's productivity. Ask
                    questions in natural language and receive AI-powered
                    insights.
                  </p>
                </motion.div>
              </div>
            </div>

            {/* Main Content */}
            <section className="py-16 px-4 sm:px-6 md:px-8 bg-white">
              <div className="max-w-7xl mx-auto">
                <div className="grid lg:grid-cols-2 gap-8 mb-16">
                  {/* Left Section - AI-Powered Conversations */}
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    className="flex flex-col justify-center"
                  >
                    <h2 className="text-3xl font-bold text-gray-900 mb-4">
                      AI-Powered Conversations
                    </h2>
                    <p className="text-gray-600 mb-8 leading-relaxed">
                      Transform how you understand your team. Ask TeamGrid AI
                      analyzes your productivity data and provides instant,
                      actionable insights through natural conversation.
                    </p>

                    {/* Key Features */}
                    <div className="space-y-6 mb-8">
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-[#00D1C1]/10 flex items-center justify-center">
                          <MessageCircle className="w-6 h-6 text-[#00D1C1]" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-1">
                            Natural Language
                          </h3>
                          <p className="text-gray-600">
                            Ask questions in plain English, no technical
                            knowledge required.
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-[#00D1C1]/10 flex items-center justify-center">
                          <Brain className="w-6 h-6 text-[#00D1C1]" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-1">
                            Smart Insights
                          </h3>
                          <p className="text-gray-600">
                            AI analyzes patterns and provides actionable
                            recommendations.
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-[#00D1C1]/10 flex items-center justify-center">
                          <TrendingUp className="w-6 h-6 text-[#00D1C1]" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-1">
                            Real-time Answers
                          </h3>
                          <p className="text-gray-600">
                            Get instant responses about team performance and
                            productivity.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* CTA Button */}
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => navigate("/book-demo")}
                      className="px-6 py-3 text-white font-semibold rounded-lg hover:shadow-lg transition-all flex items-center justify-center gap-2 w-full sm:w-auto"
                      style={{
                        background: "#00D1C1",
                        boxShadow: "0px 18px 40px rgba(0, 209, 193, 0.45)",
                      }}
                    >
                      Try Ask TeamGrid
                      <ArrowRight className="w-5 h-5" />
                    </motion.button>
                  </motion.div>

                  {/* Right Section - Chat Interface */}
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                    className="relative"
                  >
                    {/* Chat Interface */}
                    <div
                      className="relative rounded-3xl overflow-hidden flex flex-col bg-white border border-gray-200"
                      style={{
                        boxShadow:
                          "0 0 0 1px rgba(40, 180, 174, 0.05), 0 40px 100px -20px rgba(0, 0, 0, 0.1)",
                        height: "650px",
                        maxHeight: "650px",
                      }}
                    >
                      {/* Header */}
                      <div className="flex items-center gap-3 px-6 py-4 bg-gray-50/50 border-b border-gray-200">
                        <span
                          className="w-2 h-2 rounded-full"
                          style={{ background: "#4CAF50" }}
                        />

                        <div className="flex-1 flex items-center gap-2">
                          <h3 className="text-base font-semibold text-gray-900">
                            Ask TeamGrid AI
                          </h3>
                          <span
                            className="text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider text-white"
                            style={{
                              background: "#4CAF50",
                            }}
                          >
                            LIVE
                          </span>
                        </div>
                      </div>

                      {/* Chat Area */}
                      <div className="flex-1 p-6 overflow-y-auto">
                        <div className="flex flex-col gap-5">
                          {/* Sample Questions */}
                          <div className="space-y-3">
                            {sampleQuestions.map((question, idx) => {
                              const IconComponent = question.icon;
                              return (
                                <motion.div
                                  key={idx}
                                  initial={{ opacity: 0, y: 10 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  transition={{
                                    duration: 0.3,
                                    delay: 0.4 + idx * 0.1,
                                  }}
                                  className="flex items-start gap-3 cursor-pointer"
                                  onClick={() => navigate("/book-demo")}
                                >
                                  <div
                                    className={`w-8 h-8 rounded-lg ${question.bgColor} flex items-center justify-center flex-shrink-0`}
                                  >
                                    <IconComponent
                                      className={`w-4 h-4 ${question.color}`}
                                    />
                                  </div>
                                  <p className="text-sm text-gray-700 leading-tight">
                                    {question.text}
                                  </p>
                                </motion.div>
                              );
                            })}
                          </div>

                          {/* Sample Chat */}
                          <div className="flex flex-col gap-4 mt-6">
                            <div className="flex items-end gap-2">
                              <div className="w-8 h-8 rounded flex items-center justify-center text-xs font-semibold flex-shrink-0 bg-[#E0CCF2] text-[#232323]">
                                You
                              </div>
                              <div className="max-w-[78%] px-4 py-3 rounded-2xl text-sm bg-[#E0CCF2] text-[#232323]">
                                Who are the top performers this week?
                              </div>
                            </div>

                            <div className="flex items-end gap-2">
                              <div className="w-8 h-8 rounded flex items-center justify-center text-xs font-semibold flex-shrink-0 bg-[#28B4AE] text-white">
                                AI
                              </div>
                              <div className="max-w-[78%] px-4 py-3 rounded-2xl text-sm bg-white text-[#232323] border border-gray-200">
                                🏆 Sarah Rodriguez - 92% productivity
                                <br />
                                🥈 Michael Chen - 88% productivity
                                <br />
                                🥉 Emily Johnson - 85% productivity
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Input Area */}
                      <div className="px-6 py-4 pb-5 bg-gray-50/50 border-t border-gray-200">
                        <div className="flex gap-3 px-3.5 py-1.5 rounded-xl bg-white border border-gray-200">
                          <div className="flex-1">
                            <input
                              type="text"
                              placeholder="Ask anything about your team..."
                              className="w-full bg-transparent border-none outline-none text-sm text-gray-900 placeholder-gray-500"
                              disabled
                            />
                          </div>
                          <button
                            type="button"
                            className="w-10 h-10 rounded-lg flex items-center justify-center transition-all bg-transparent hover:bg-gray-100"
                          >
                            <svg
                              className="w-5 h-5 text-gray-700"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
                              />
                            </svg>
                          </button>
                          <button
                            type="button"
                            className="w-10 h-10 rounded-lg flex items-center justify-center transition-all bg-transparent hover:bg-gray-100"
                          >
                            <svg
                              className="w-5 h-5 text-gray-700"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                              />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </div>
            </section>

            {/* Testimonials Section */}
            <Testimonials />

            {/* FAQ Section - full-bleed gray bg (match other Feature pages) */}
            <div className="relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] w-screen py-16 bg-[#F3F4F6]">
              <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  className="text-center mb-12"
                >
                  <span className="inline-block px-5 py-2.5 rounded-full text-xs font-semibold uppercase tracking-wide text-[#0D9488] bg-[#CCFBF1] border border-[#99F6E4] mb-6">
                    GOT QUESTIONS?
                  </span>
                  <h2 className="text-[24px] sm:text-[28px] md:text-[32px] lg:text-[36px] font-bold text-black mb-4">
                    Frequently Asked Questions
                  </h2>
                  <p className="text-lg text-gray-600">
                    Everything you need to know about TeamGrid's AI assistant.
                  </p>
                </motion.div>

                <div className="space-y-4 w-full max-w-[720px] mx-auto">
                  {askTeamGridFAQs.map((faq, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      className="w-full rounded-xl p-6 cursor-pointer bg-white"
                      style={{
                        border: faqOpen[index]
                          ? "1px solid #28B4AE"
                          : "1px solid #D1D5DB",
                        transition: "border-color 0.15s ease-in-out",
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => toggleFaq(index)}
                        className="w-full flex items-center justify-between text-left"
                      >
                        <h3
                          className="text-left font-bold text-[#1A1A1A] flex-1"
                          style={{
                            fontFamily: "Inter, sans-serif",
                            fontSize: 16,
                            lineHeight: "26px",
                          }}
                        >
                          {faq.question}
                        </h3>
                        <span className="flex-shrink-0">
                          {faqOpen[index] ? (
                            <Minus className="w-5 h-5 text-[#111111]" />
                          ) : (
                            <Plus className="w-5 h-5 text-[#111111]" />
                          )}
                        </span>
                      </button>
                      <AnimatePresence>
                        {faqOpen[index] && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3, ease: "easeInOut" }}
                            className="overflow-hidden"
                          >
                            <p
                              className="mt-4"
                              style={{
                                fontFamily: "Inter, sans-serif",
                                fontWeight: 400,
                                fontSize: 16,
                                lineHeight: "26px",
                                color: "#374151",
                              }}
                            >
                              {faq.answer}
                            </p>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>

            <ReadyToTransform
              title="Ready to Transform Your Team?"
              description="Join thousands of teams using TeamGrid to boost productivity, eliminate guesswork, and make data-driven decisions."
              // primaryButtonText="Start Free Trial"
              primaryButtonText="Register"
              primaryButtonLink="/register"
              secondaryButtonText="Request Demo"
              secondaryButtonLink="/book-demo"
              showAdditionalInfo={true}
            />

            <Footer />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AskTeamGrid;
